// src/app/time-attendance/services/daily-punches.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, EMPTY, expand, reduce } from 'rxjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import { environment } from '../../../environments/environment';
import { DateUtils } from '../utils/date-utils';
import {
  ApiCompanyListItem,
  ApiDepartmentListItem,
  ApiPagedResponse,
  DailyPunchFilters,
  EmployeeDailyPunch,
  OffTimeApprovalStatus,
  OffTimeBlock,
  PunchBlock,
  ScheduledBlock,
} from '../models/daily-punch.model';

dayjs.extend(isoWeek);

interface WeeklyReportResponse {
  status: 'success' | 'error';
  data: {
    employeeCount?: number;
    payableHoursSync?: unknown | null;
    timeLogs?: WeeklyEmployeeWrapper[];
    userSchedules?: WeeklyScheduleWrapper[];
    dayOffRequests?: WeeklyDayOffRequestWrapper[];
  };
}

interface WeeklyEmployeeWrapper {
  user: WeeklyEmployeeUser;
}

interface WeeklyEmployeeUser {
  id: number;
  firstName: string;
  lastName: string;
  employeeNumber?: string | null;
  rfid?: string | null;
  logs?: DailyLog[] | null;
}

interface DailyLog {
  date: string;
  timeLogs: TimeLog[];
}

interface WeeklyReportClockLog {
  id: number;
  logTime: string;
  timeZoneId?: string | null;
}

interface TimeLog {
  id: number;
  hours?: number;
  totalHours?: number;
  clockIn: WeeklyReportClockLog | null;
  clockOut: WeeklyReportClockLog | null;
}

interface WeeklyScheduleWrapper {
  user: WeeklyScheduleUser;
  schedules?: ScheduleAssignment[] | null;
  userAssignedSchedules?: ScheduleAssignment[] | null;
}

interface WeeklyScheduleUser {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  employeeNumber?: string | null;
  rfid?: string | null;
  schedules?: ScheduleAssignment[] | null;
  userAssignedSchedules?: ScheduleAssignment[] | null;
}

interface ScheduleAssignment {
  id: number;
  yyww: string;
  assignedDate: string;
  schedule: {
    id: number;
    localStartTime: string;
    localEndTime: string;
    timeZoneId?: string | null;
    isOverNight: boolean;
    breakTime?: string | null;
    breakThresholdHours?: number | null;
  };
}

interface WeeklyDayOffRequestWrapper {
  user: WeeklyDayOffRequestUser;
}

interface WeeklyDayOffRequestUser {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  employeeNumber?: string | null;
  rfid?: string | null;
  dayOffRequests?: DayOffRequestItem[] | null;
}

interface DayOffRequestItem {
  id: number;
  yyww?: string | null;
  startTime: string;
  endTime: string;
  isApproved: boolean | null;
  type?: string | null;
  requestType?: string | null;
  leaveType?: string | null;
  offTimeType?: string | null;
  payCode?: { id?: number; name?: string | null } | null;
  leavePolicy?: { id?: number; name?: string | null } | null;
}

@Injectable({ providedIn: 'root' })
export class DailyPunchesService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  getDailyPunches(date: Date): Observable<EmployeeDailyPunch[]> {
    const weekInfo = this.getWeekInfoFromDate(date);

    return this.getDailyPunchesByFilters(date, {
      fullYearWeek: weekInfo.yyyyww,
    });
  }

  getDailyPunchesByFilters(date: Date, filters: DailyPunchFilters): Observable<EmployeeDailyPunch[]> {
    const params = this.buildWeeklyReportParams({
      ...filters,
      fullYearWeek: filters.fullYearWeek ?? this.getWeekInfoFromDate(date).yyyyww,
    });

    return this.http
      .get<WeeklyReportResponse>(`${this.baseUrl}/api/employee-time-logs/weekly-report`, { params })
      .pipe(map((response) => this.mapWeeklyReportToDailyPunches(response, date)));
  }

  private mapWeeklyReportToDailyPunches(
    response: WeeklyReportResponse,
    selectedDate: Date,
  ): EmployeeDailyPunch[] {
    if (response?.status !== 'success' || !response.data) {
      return [];
    }

    const usersById = new Map<number, WeeklyEmployeeUser>();
    const punchesByUserId = new Map<number, PunchBlock[]>();
    const schedulesByUserId = new Map<number, ScheduledBlock[]>();
    const offTimesByUserId = new Map<number, OffTimeBlock[]>();
    const scheduledUserIdsForSelectedDay = new Set<number>();

    for (const wrapper of response.data.userSchedules ?? []) {
      const user = wrapper?.user;

      if (!user?.id) {
        continue;
      }

      this.upsertUser(usersById, {
        id: user.id,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        employeeNumber: user.employeeNumber ?? null,
        rfid: user.rfid ?? null,
        logs: [],
      });

      const assignments = this.getScheduleAssignments(wrapper);
      const blocks = this.mapSchedulesOverlappingSelectedDay(assignments, selectedDate);

      schedulesByUserId.set(user.id, blocks);

      if (blocks.length > 0) {
        scheduledUserIdsForSelectedDay.add(user.id);
      }
    }

    for (const wrapper of response.data.timeLogs ?? []) {
      const user = wrapper?.user;

      if (!user?.id) {
        continue;
      }

      this.upsertUser(usersById, user);

      const allPunches: PunchBlock[] = [];

      for (const dayLog of user.logs ?? []) {
        for (const log of dayLog.timeLogs ?? []) {
          const punch = this.mapTimeLog(log);

          if (punch) {
            allPunches.push(punch);
          }
        }
      }

      punchesByUserId.set(user.id, this.filterPunchesForDay(allPunches, selectedDate));
    }

    for (const wrapper of response.data.dayOffRequests ?? []) {
      const user = wrapper?.user;

      if (!user?.id) {
        continue;
      }

      this.upsertUser(usersById, {
        id: user.id,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        employeeNumber: user.employeeNumber ?? null,
        rfid: user.rfid ?? null,
        logs: [],
      });

      const blocks = this.mapDayOffRequestsOverlappingSelectedDay(
        user.dayOffRequests ?? [],
        selectedDate,
      );

      offTimesByUserId.set(user.id, blocks);
    }

    const allUserIds = new Set<number>([
      ...usersById.keys(),
      ...punchesByUserId.keys(),
      ...schedulesByUserId.keys(),
      ...offTimesByUserId.keys(),
      ...scheduledUserIdsForSelectedDay,
    ]);

    return Array.from(allUserIds)
      .map((userId): EmployeeDailyPunch => {
        const user = usersById.get(userId);

        return {
          id: userId,
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          employeeNumber: user?.employeeNumber ?? null,
          rfid: user?.rfid ?? null,
          punches: punchesByUserId.get(userId) ?? [],
          schedules: schedulesByUserId.get(userId) ?? [],
          offTimes: offTimesByUserId.get(userId) ?? [],
        };
      })
      .filter(
        (employee) =>
          scheduledUserIdsForSelectedDay.has(employee.id) ||
          employee.punches.length > 0 ||
          employee.offTimes.length > 0,
      )
      .sort((a, b) => {
        const lastNameCompare = String(a.lastName ?? '').localeCompare(String(b.lastName ?? ''));

        return lastNameCompare !== 0
          ? lastNameCompare
          : String(a.firstName ?? '').localeCompare(String(b.firstName ?? ''));
      });
  }

  private getScheduleAssignments(wrapper: WeeklyScheduleWrapper): ScheduleAssignment[] {
    return [
      ...(wrapper.user?.schedules ?? []),
      ...(wrapper.user?.userAssignedSchedules ?? []),
      ...(wrapper.schedules ?? []),
      ...(wrapper.userAssignedSchedules ?? []),
    ];
  }

  private upsertUser(usersById: Map<number, WeeklyEmployeeUser>, user: WeeklyEmployeeUser): void {
    const existing = usersById.get(user.id);

    usersById.set(user.id, {
      id: user.id,
      firstName: existing?.firstName || user.firstName || '',
      lastName: existing?.lastName || user.lastName || '',
      employeeNumber: existing?.employeeNumber ?? user.employeeNumber ?? null,
      rfid: existing?.rfid ?? user.rfid ?? null,
      logs: existing?.logs ?? user.logs ?? [],
    });
  }

  private buildWeeklyReportParams(filters: DailyPunchFilters): HttpParams {
    let params = new HttpParams();

    const fullYearWeek = String(filters.fullYearWeek ?? '').trim();
    const searchTerm = String(filters.searchTerm ?? '').trim();

    if (fullYearWeek) {
      params = params.set('FullYearWeek', fullYearWeek);
    }

    if (filters.companyIds?.length) {
      params = params.set('companyIds', filters.companyIds.join(','));
    }

    if (filters.departmentIds?.length) {
      params = params.set('departmentIds', filters.departmentIds.join(','));
    }

    if (typeof filters.employeeId === 'number') {
      params = params.set('employeeId', String(filters.employeeId));
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return params;
  }

  getDepartmentsPage(
    page: number,
    pageSize = 200,
    searchTerm = '',
  ): Observable<ApiPagedResponse<ApiDepartmentListItem>> {
    let params = new HttpParams().set('pageNumber', String(page)).set('pageSize', String(pageSize));

    const term = String(searchTerm ?? '').trim();

    if (term) {
      params = params.set('searchTerm', term);
    }

    return this.http.get<ApiPagedResponse<ApiDepartmentListItem>>(
      `${this.baseUrl}/api/departments`,
      { params },
    );
  }

  getAllDepartments(pageSize = 200, searchTerm = ''): Observable<ApiDepartmentListItem[]> {
    return this.getAllPaged<ApiDepartmentListItem>((page) =>
      this.getDepartmentsPage(page, pageSize, searchTerm),
    ).pipe(
      map((items) =>
        [...items].sort((a, b) => {
          const byName = String(a.name ?? '').localeCompare(String(b.name ?? ''));

          return byName !== 0 ? byName : a.id - b.id;
        }),
      ),
    );
  }

  getCompaniesPage(
    page: number,
    pageSize = 200,
    searchTerm = '',
  ): Observable<ApiPagedResponse<ApiCompanyListItem>> {
    let params = new HttpParams().set('pageNumber', String(page)).set('pageSize', String(pageSize));

    const term = String(searchTerm ?? '').trim();

    if (term) {
      params = params.set('searchTerm', term);
    }

    return this.http.get<ApiPagedResponse<ApiCompanyListItem>>(`${this.baseUrl}/api/companies`, {
      params,
    });
  }

  getAllCompanies(pageSize = 200, searchTerm = ''): Observable<ApiCompanyListItem[]> {
    return this.getAllPaged<ApiCompanyListItem>((page) =>
      this.getCompaniesPage(page, pageSize, searchTerm),
    ).pipe(
      map((items) =>
        [...items].sort((a, b) => {
          const byName = String(a.name ?? '').localeCompare(String(b.name ?? ''));

          return byName !== 0 ? byName : a.id - b.id;
        }),
      ),
    );
  }

  private getAllPaged<T>(
    fetchPage: (page: number) => Observable<ApiPagedResponse<T>>,
  ): Observable<T[]> {
    return fetchPage(1).pipe(
      expand((res) => {
        const nextPage = (res?.currentPage ?? 1) + 1;
        const totalPages = res?.totalPages ?? 1;

        return nextPage <= totalPages ? fetchPage(nextPage) : EMPTY;
      }),
      reduce((acc: T[], res) => {
        if (res?.status === 'success') {
          acc.push(...(res.data ?? []));
        }

        return acc;
      }, []),
    );
  }

  private mapTimeLog(log: TimeLog): PunchBlock | null {
    const inTime = log.clockIn?.logTime;

    if (!inTime) {
      return null;
    }

    const startDate = this.parseBackendUtcDate(inTime);
    const outTime = log.clockOut?.logTime ?? null;
    const endDate = outTime ? this.parseBackendUtcDate(outTime) : undefined;

    return {
      startDate,
      endDate,
      isActive: !endDate,
    };
  }

  private filterPunchesForDay(punches: PunchBlock[], selectedDate: Date): PunchBlock[] {
    const dayStart = dayjs(selectedDate).startOf('day');
    const dayEnd = dayjs(selectedDate).endOf('day');

    return punches
      .filter((punch) => {
        const start = dayjs(punch.startDate);
        const end = punch.endDate ? dayjs(punch.endDate) : dayjs();

        return start.isBefore(dayEnd) && end.isAfter(dayStart);
      })
      .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf());
  }

  private mapDayOffRequestsOverlappingSelectedDay(
    requests: DayOffRequestItem[],
    selectedDate: Date,
  ): OffTimeBlock[] {
    const dayStart = dayjs(selectedDate).startOf('day');
    const dayEnd = dayjs(selectedDate).endOf('day');

    return (requests ?? [])
      .map((request) => this.mapDayOffRequest(request))
      .filter(
        (block) =>
          !Number.isNaN(block.startDate.getTime()) && !Number.isNaN(block.endDate.getTime()),
      )
      .filter(
        (block) =>
          dayjs(block.startDate).isBefore(dayEnd) && dayjs(block.endDate).isAfter(dayStart),
      )
      .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf());
  }

  private mapDayOffRequest(request: DayOffRequestItem): OffTimeBlock {
    const type = this.getDayOffTypeName(request);
    const status = this.getDayOffApprovalStatus(request.isApproved);

    return {
      id: request.id,
      startDate: this.parseBackendLocalDate(request.startTime),
      endDate: this.parseBackendLocalDate(request.endTime),
      type,
      status,
      isApproved: request.isApproved,
      colorKey: this.getOffTimeColorKey(type, status),
    };
  }

  private getDayOffTypeName(request: DayOffRequestItem): string {
    const directType =
      request.type ??
      request.requestType ??
      request.leaveType ??
      request.offTimeType ??
      request.payCode?.name ??
      request.leavePolicy?.name ??
      null;

    return String(directType ?? '').trim() || 'Off Time';
  }

  private getDayOffApprovalStatus(value: boolean | null): OffTimeApprovalStatus {
    if (value === true) return 'approved';
    if (value === false) return 'rejected';
    if (value === null) return 'pending';

    return 'unknown';
  }

  private getOffTimeColorKey(type: string, status: OffTimeApprovalStatus): string {
    const normalizedType = String(type ?? '').trim().toLowerCase();

    if (normalizedType.includes('vacation') || normalizedType.includes('pto')) return 'vacation';
    if (normalizedType.includes('sick')) return 'sick';
    if (normalizedType.includes('holiday')) return 'holiday';
    if (normalizedType.includes('personal')) return 'personal';
    if (normalizedType.includes('bereav')) return 'bereavement';
    if (status === 'pending') return 'pending';
    if (status === 'rejected') return 'rejected';

    return 'offtime';
  }

  private mapSchedulesOverlappingSelectedDay(
    assignments: ScheduleAssignment[],
    selectedDate: Date,
  ): ScheduledBlock[] {
    const dayStart = dayjs(selectedDate).startOf('day');
    const dayEnd = dayjs(selectedDate).endOf('day');

    const allBlocks = (assignments ?? [])
      .filter((assignment) => !!assignment?.schedule && !!assignment?.assignedDate)
      .map((assignment) => this.mapScheduleAssignment(assignment))
      .filter(
        (block) =>
          !Number.isNaN(block.startDate.getTime()) && !Number.isNaN(block.endDate.getTime()),
      );

    const overlapping = allBlocks.filter(
      (block) => dayjs(block.startDate).isBefore(dayEnd) && dayjs(block.endDate).isAfter(dayStart),
    );

    return this.mergeOverlappingScheduleBlocks(this.dedupeScheduleBlocks(overlapping));
  }

  private mapScheduleAssignment(assignment: ScheduleAssignment): ScheduledBlock {
    const start = this.buildLocalDateTime(assignment.assignedDate, assignment.schedule.localStartTime);
    let end = this.buildLocalDateTime(assignment.assignedDate, assignment.schedule.localEndTime);

    if (assignment.schedule.isOverNight || dayjs(end).isBefore(dayjs(start))) {
      end = dayjs(end).add(1, 'day').toDate();
    }

    return {
      startDate: start,
      endDate: end,
      isOvernight: dayjs(end).startOf('day').isAfter(dayjs(start).startOf('day')),
      scheduleId: assignment.schedule.id,
      assignmentId: assignment.id,
    };
  }

  private dedupeScheduleBlocks(blocks: ScheduledBlock[]): ScheduledBlock[] {
    const seen = new Set<string>();
    const output: ScheduledBlock[] = [];

    for (const block of blocks) {
      const key = [
        block.scheduleId,
        dayjs(block.startDate).valueOf(),
        dayjs(block.endDate).valueOf(),
      ].join('|');

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      output.push(block);
    }

    return output;
  }

  private mergeOverlappingScheduleBlocks(blocks: ScheduledBlock[]): ScheduledBlock[] {
    const sorted = [...blocks].sort(
      (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
    );

    const merged: ScheduledBlock[] = [];

    for (const current of sorted) {
      const last = merged[merged.length - 1];

      if (!last) {
        merged.push({ ...current });
        continue;
      }

      const currentStart = dayjs(current.startDate);
      const currentEnd = dayjs(current.endDate);
      const lastEnd = dayjs(last.endDate);

      if (currentStart.isSame(lastEnd) || currentStart.isBefore(lastEnd)) {
        last.endDate = currentEnd.isAfter(lastEnd) ? current.endDate : last.endDate;
        last.isOvernight =
          last.isOvernight ||
          dayjs(last.endDate).startOf('day').isAfter(dayjs(last.startDate).startOf('day'));

        continue;
      }

      merged.push({ ...current });
    }

    return merged;
  }

  private buildLocalDateTime(dateYmd: string, timeHms: string): Date {
    return new Date(`${this.toDateKey(dateYmd)}T${timeHms}`);
  }

  private parseBackendUtcDate(iso: string): Date {
    const value = String(iso ?? '').trim();

    if (!value) {
      return new Date(NaN);
    }

    return this.hasOffsetOrZ(value) ? new Date(value) : new Date(`${value}Z`);
  }

  private parseBackendLocalDate(iso: string): Date {
    const value = String(iso ?? '').trim();

    return value ? new Date(value) : new Date(NaN);
  }

  private hasOffsetOrZ(value: string): boolean {
    return /Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(value);
  }

  private toDateKey(value: string | null | undefined): string {
    return String(value ?? '').slice(0, 10);
  }

  private getWeekInfoFromDate(date: Date) {
    const d = dayjs(date);
    const year = d.isoWeekYear();
    const week = d.isoWeek();

    return { ...DateUtils['formatWeek'](year, week) };
  }
}